/**
 * A page header, and the pieces every screen repeats: a title with an action,
 * a row of stat tiles, an empty state, and a back link.
 *
 * The tiles are not decoration. Every list screen opens with them because a
 * search box over a bare table tells you nothing about what you are looking at
 * until you have read it.
 */
import type { ReactNode } from "react";
import { Link } from "../lib/router.tsx";

export function PageHead({ title, sub, actions }: { title: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="row-between page-head">
      <div className="stack-sm">
        <h1>{title}</h1>
        {sub && <p className="muted small">{sub}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="back-link">
      ← {children}
    </Link>
  );
}

export type TileTone = "accent" | "ok" | "warn" | "bad" | "info" | "muted";

const TONE_BARS: Record<TileTone, string> = {
  accent: "var(--accent)",
  ok: "var(--ok-bar)",
  warn: "var(--warn-bar)",
  bad: "var(--bad-bar)",
  info: "var(--info-bar)",
  muted: "var(--muted-bar)",
};

export function Tiles({ tiles }: { tiles: { label: string; value: ReactNode; tone?: TileTone }[] }) {
  return (
    <div className="tiles">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="tile"
          style={{ ["--tile-bar" as string]: TONE_BARS[tile.tone ?? "accent"] }}
        >
          <div className="tile-value">{tile.value}</div>
          <div className="tile-label">{tile.label}</div>
        </div>
      ))}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="card">
      <div className="empty">
        <h3>{title}</h3>
        {children && <p className="small">{children}</p>}
      </div>
    </div>
  );
}

export function Problem({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return <div className="notice notice-bad">{message}</div>;
}

export function Loading({ what }: { what: string }) {
  return <div className="empty">Loading {what}…</div>;
}

/**
 * A screen this person may not open.
 *
 * Reachable by URL - a bookmark, a shared link, or the address you were on
 * when your permissions changed - so it needs to say what is missing rather
 * than render an empty version of the page, which reads as "there is nothing
 * here" when the truth is "you cannot see what is here".
 */
export function NotPermitted({ what }: { what: string }) {
  return (
    <div className="stack">
      <h1>Not available to you</h1>
      <div className="card">
        <div className="empty">
          <h3>Your account cannot see {what}</h3>
          <p className="small">An administrator can grant that from the accounts screen.</p>
        </div>
      </div>
    </div>
  );
}

/** The first thing a new person should see, on both overviews. */
export function GuideInvite() {
  return (
    <Link to="/guide" className="card guide-invite">
      <span className="play" aria-hidden="true">▶</span>
      <span className="stack-sm">
        <strong>New here? Start with the walkthrough</strong>
        <span className="small muted">
          Two minutes on building a product and changing a live one, then the written version.
        </span>
      </span>
    </Link>
  );
}
